import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import SetupPassword from './pages/SetupPassword';
import Homepage from './pages/Homepage';
import EnvironmentBanner from './components/EnvironmentBanner';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CandidateGraph from './pages/CandidateGraph';
import CandidateGraphClassic from './pages/CandidateGraphClassic';
import TargetedOutreach from './pages/TargetedOutreach';
import DeepResearch from './pages/DeepResearch';
import Performance from './pages/Performance';
import AdminConsole from './pages/AdminConsole';
import ManageTeam from './pages/ManageTeam';
import HelpSupport from './pages/HelpSupport';
import DataPage from './pages/DataPage';
import SignalSourcesPage from './pages/SignalSourcesPage';
import AtsConnectionForm from './components/AtsConnectionForm';
import NotFound from './pages/NotFound';
import ProtectedRoute, { isSignalCueAppHost } from './components/ProtectedRoute';
import { authService } from './services/authService';
import { FlagProvider, FLAGS } from './FlagProvider';
import UserbackProvider from './UserbackProvider';
import { FEATURES, homeFor } from './lib/permissions';
import { APP_URL } from './config';

// Component to handle token from URL parameters (for cross-domain auth)
function AuthHandler({ children }) {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const userStr = urlParams.get('user');

    if (token && userStr) {
      // Store token and user in sessionStorage
      sessionStorage.setItem('signalcue_token', token);
      sessionStorage.setItem('signalcue_user', userStr);
      
      // Remove token from URL for security
      const newParams = new URLSearchParams(urlParams);
      newParams.delete('token');
      newParams.delete('user');
      
      const newSearch = newParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location]);

  return children;
}

function App() {
  // Check if we're on the public domain (signalcue.ai) vs app domain (app.signalcue.ai)
  // In production: signalcue.ai is public, app.signalcue.ai is app
  // In local dev: localhost:3000 could be either, but we'll treat it as app domain
  const hostname = window.location.hostname;
  const isLocalDev = hostname.includes('localhost') || hostname.includes('127.0.0.1');
  const isPublicDomain = hostname === 'signalcue.ai' || !isSignalCueAppHost(hostname);
  
  return (
    <FlagProvider>
      {/* OUTSIDE BrowserRouter on purpose: which deployment you are looking at
          does not depend on the route, and it must be visible on the login page
          too -- the login screen is exactly where someone types credentials into
          the wrong environment. It needs no router context. */}
      <EnvironmentBanner />
      <BrowserRouter>
        <AuthHandler>
          {/* Inside BrowserRouter because it needs useLocation.

              Its position relative to AuthHandler is NOT what makes the
              cross-domain handoff work, and an earlier version of this comment
              claimed it was. React commits CHILD effects before parent ones, so
              as AuthHandler's child the provider's effect actually runs FIRST --
              before the session is written. What saves it is that the provider
              reads the session AFTER awaiting the widget's initialisation
              promise, and that await defers the read past the whole commit
              phase, so AuthHandler's synchronous sessionStorage write always
              lands first however the two are nested.

              That is worth knowing before moving the read: hoisting
              identityOf(authService.getUser()) above the await would reintroduce
              the bug the nesting was mistakenly credited with preventing -- a
              user arriving on the handoff URL and filing from that landing page
              would report anonymously, and stay anonymous until they navigated,
              because AuthHandler strips the token with history.replaceState,
              which react-router does not observe.

              It consumes no flag, so its position inside FlagProvider is
              incidental. It is deliberately NOT in FLAGS: the token is already
              the switch, and a second switch is a second thing that can
              disagree. */}
          <UserbackProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/setup-password" element={<SetupPassword />} />
              {isPublicDomain ? (
                // On public domain, show homepage or redirect authenticated users to app domain
                <>
                  <Route path="/" element={<Homepage />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route 
                    path="*" 
                    element={
                      <PublicDomainHandler />
                    } 
                  />
                </>
              ) : (
                // On app domain, show protected routes
                <>
                  {isLocalDev && (
                    <>
                      <Route path="/" element={<Homepage />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                    </>
                  )}
                  <Route
                    path="/candidates"
                    element={
                      <ProtectedRoute feature={FEATURES.candidateGraph} flag={FLAGS.candidateGraph}>
                        <CandidateGraph />
                      </ProtectedRoute>
                    }
                  />
                  {/* The pre-redesign table, kept alive on the same fit-v2.0 rows so
                      the two presentations can be compared on identical data. */}
                  <Route
                    path="/candidates/classic"
                    element={
                      <ProtectedRoute feature={FEATURES.candidateGraph} flag={FLAGS.candidateGraph}>
                        <CandidateGraphClassic />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/targeted-outreach"
                    element={
                      <ProtectedRoute feature={FEATURES.targetedOutreach} flag={FLAGS.targetedOutreach}>
                        <TargetedOutreach />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/deep-research"
                    element={
                      <ProtectedRoute feature={FEATURES.deepResearch}>
                        <DeepResearch />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/performance" 
                    element={
                      <ProtectedRoute>
                        <Performance />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/manage-team"
                    element={
                      <ProtectedRoute feature={FEATURES.manageTeam}>
                        <ManageTeam />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute feature={FEATURES.adminConsole}>
                        <AdminConsole />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/data"
                    element={
                      <ProtectedRoute feature={FEATURES.dataQuality}>
                        <DataPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Signal Sources gets its own top-level page rather than an Admin
                      Console tab: spec SS9 is "one page, no tabs", and it is a
                      tenant-facing view like Data, not an admin setting. The
                      component here is still the pre-redesign one, so it reports a
                      fetch failure until V4 rebuilds the API and V5 the page. */}
                  <Route
                    path="/admin/sources"
                    element={
                      <ProtectedRoute feature={FEATURES.signalSources} flag={FLAGS.signalSources}>
                        <SignalSourcesPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* The DIRECT-DATABASE ATS connection (Task 5's PTA design) that the
                      nightly per-tenant sweep actually reads -- a new surface, not the
                      REST-API integration path IntegrationList/IntegrationConfig/
                      IntegrationLogs configure, which has never run anywhere. Gated on
                      admin_console rather than a new feature: the backend guards all
                      four /api/ats-connection routes with an admin-only role check, so
                      reusing the existing gate needs no internal/authz change and no
                      permissions.json regeneration. */}
                  <Route
                    path="/admin/ats"
                    element={
                      <ProtectedRoute feature={FEATURES.adminConsole}>
                        <AtsConnectionForm />
                      </ProtectedRoute>
                    }
                  />
                  {/* Quality and the pipeline are one page now; keep old links working. */}
                  <Route path="/admin/data-quality" element={<Navigate to="/admin/data" replace />} />
                  <Route path="/admin/pipeline" element={<Navigate to="/admin/data" replace />} />
                  {/* /help and /performance carry no feature: every signed-in role
                      may reach them. */}
                  <Route
                    path="/help"
                    element={
                      <ProtectedRoute>
                        <HelpSupport />
                      </ProtectedRoute>
                    }
                  />
                  {/* The bare app-domain root. /candidates is not admin's surface any
                      more, so a SIGNED-IN visitor reads the same table the nav and
                      the router do.

                      A SIGNED-OUT one must not: homeFor(undefined) is '/login', and
                      that fallback means "a role with no surfaces", not "no user".
                      Taking it here would serve a login form on the APP host
                      (app.signalcue.ai) when LOGIN_URL is deliberately the marketing
                      host (signalcue.ai) — a second login origin nobody decided to
                      create. Routing an unauthenticated visitor through a PROTECTED
                      page instead is what preserves the old behaviour exactly:
                      ProtectedRoute sees no session and does the LOGIN_URL bounce,
                      which is where a signed-out visitor has always ended up. The
                      target is never rendered, so it does not matter that a future
                      admin cannot open /candidates.

                      Only registered off localhost, so no jsdom test can reach this
                      line; it is verified by inspection. See the header of
                      e2e/role-permissions.spec.js. */}
                  {!isLocalDev && (
                    <Route
                      path="/"
                      element={
                        <Navigate
                          to={authService.isAuthenticated() ? homeFor(authService.getUser()?.role) : '/candidates'}
                          replace
                        />
                      }
                    />
                  )}
                  {/* Any other URL on the app domain. Before this existed an unknown path
                      matched nothing and rendered a blank screen. It is also what a
                      feature-denied route renders, so the two are indistinguishable. */}
                  <Route path="*" element={<NotFound />} />
                </>
              )}
            </Routes>
          </UserbackProvider>
        </AuthHandler>
      </BrowserRouter>
    </FlagProvider>
  );
}

// Component to handle public domain - redirect authenticated users to app domain
function PublicDomainHandler() {
  const location = useLocation();
  
  useEffect(() => {
    // If authenticated and on public domain, redirect to app domain
    if (authService.isAuthenticated() && location.pathname !== '/login' && location.pathname !== '/') {
      window.location.href = APP_URL;
    }
  }, [location]);
  
  // If not on login or homepage, redirect to homepage
  if (location.pathname !== '/login' && location.pathname !== '/') {
    return <Navigate to="/" replace />;
  }
  
  return null;
}

export default App;
